/**
 * The distinct id for a visitor arriving from a TikTok ad, derived from the URL rather than a cookie.
 *
 * TikTok's in-app ad browser drops the cookies the edge sets, so the id the middleware minted never
 * reaches posthog-js. The URL does arrive, so both sides derive the id from the click id in it.
 *
 * Gated on that browser's user agent so a forwarded ad URL opened in a normal browser cannot inherit
 * the original visitor's identity — the id doubles as the lead's connection_id and the pixel dedup
 * key. That covers the accidental case only; a user agent is self-reported.
 */

const TIKTOK_CLICK_ID_PARAM = 'ttclid';
const TIKTOK_DISTINCT_ID_PREFIX = 'tt_';
const TIKTOK_IN_APP_UA = /musical_ly|BytedanceWebview/i;

// FNV-1a run twice from different offset bases, concatenated. Lane B's bit 0 is always lane A's
// complement (an odd prime preserves parity), so the id carries 63 effective bits — ample here.
const FNV_PRIME_32 = 0x01000193;
const FNV_OFFSET_A = 0x811c9dc5;
const FNV_OFFSET_B = (FNV_OFFSET_A ^ 0x5bd1e995) >>> 0;
const encoder = new TextEncoder();

function fnv1a32Hex(bytes: Uint8Array, offsetBasis: number): string {
	let hash = offsetBasis;
	for (const byte of bytes) {
		hash ^= byte;
		hash = Math.imul(hash, FNV_PRIME_32);
	}
	return (hash >>> 0).toString(16).padStart(8, '0');
}

/** Click ids run past PostHog's 200-character distinct_id cap, hence a hash. */
function hash64Hex(input: string): string {
	const bytes = encoder.encode(input);
	return fnv1a32Hex(bytes, FNV_OFFSET_A) + fnv1a32Hex(bytes, FNV_OFFSET_B);
}

/**
 * Shortest click id we treat as identifying one ad click. TikTok's ad-preview ids are shared by
 * everyone opening the preview rather than issued per click, and one reached a live campaign —
 * hashed, those visitors collapse into one person, one lead connection_id, one pixel dedup key.
 * Previews are 74 characters and real click ids 100 or more, so the floor sits in the gap.
 */
const MIN_CLICK_ID_LENGTH = 90;

/**
 * The LAST usable click id, where `params.get()` would take the first. Guards a destination URL
 * configured with one already in it: the platform's own arrives last. No URL of ours has had two.
 */
function lastClickId(params: URLSearchParams): string | undefined {
	const clickIds = params
		.getAll(TIKTOK_CLICK_ID_PARAM)
		.map((value) => value.trim())
		.filter((value) => value.length >= MIN_CLICK_ID_LENGTH);

	return clickIds[clickIds.length - 1];
}

/** Undefined outside TikTok's in-app browser, or when the URL carries no usable click id. */
export function tikTokDistinctIdFromRequest(
	params: URLSearchParams,
	userAgent: string | null | undefined,
): string | undefined {
	if (!userAgent || !TIKTOK_IN_APP_UA.test(userAgent)) return undefined;

	const clickId = lastClickId(params);
	return clickId ? `${TIKTOK_DISTINCT_ID_PREFIX}${hash64Hex(clickId)}` : undefined;
}
